import { type TableColumn } from 'src/shared/components/Table/Table';
import { useTranslation } from 'react-i18next';
import {
  TRANSACTION_STATUS,
  type FlexflowTransaction,
  type WithoutApproved,
  type TRANSACTION_CATEGORY,
  TRANSACTION_KIND,
  TRANSACTION_TYPE,
  WALLET_TRANSACTION_TYPE
} from './types';
import TransactionCategory from '../components/TransactionCategory';
import TransactionStatus from '../components/TransactionStatus';
import { type CountryCodes, type CurrencyCodes, formatCurrency, invertSign } from 'src/shared/utils/currency';
import { InfoTooltip } from './styles';
import { datePrettifier } from 'src/modules/DataReview/helpers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { mapApiEnumValueToTranslationKey } from './utils/translationKeys';
import { getAmountTooltipText } from './utils/amountTooltip';

const mapStatus = (status: TRANSACTION_STATUS): WithoutApproved => {
  if (status === TRANSACTION_STATUS.APPROVED) {
    return TRANSACTION_STATUS.PENDING;
  }

  return status;
};

export const DATE_FORMAT = 'YYYY-MM-DD';

export const useGetColumns = (
  locale: CountryCodes,
  currency: CurrencyCodes
): Array<TableColumn<FlexflowTransaction>> => {
  const { t } = useTranslation(['history']);

  return [
    {
      accessor: 'requestedAt-date',
      title: t('table headers.date'),
      renderCell: (row: FlexflowTransaction) => {
        return <>{datePrettifier(row.requestedAt)}</>;
      }
    },
    {
      accessor: 'name',
      title: t('table headers.full name')
    },
    {
      accessor: 'personalId',
      title: t('table headers.personal id')
    },
    {
      accessor: 'category',
      title: t('table headers.category'),
      renderCell: (row: FlexflowTransaction) => {
        const rowCategory = row.category.toLocaleLowerCase() as TRANSACTION_CATEGORY;
        // For transactions, use transactionType, for payments, check amount sign
        const isCredit = row.transactionType
          ? row.transactionType === TRANSACTION_TYPE.CREDIT
          : invertSign(Number(row.amount)) > 0;
        return <TransactionCategory category={rowCategory} credit={isCredit} />;
      }
    },
    {
      accessor: 'transactionKind',
      title: t('table headers.transaction kind'),
      renderCell: (row: FlexflowTransaction) => {
        if (!row.transactionKind) {
          return <div>-</div>;
        }

        return <>{t(`transaction kind.values.${mapApiEnumValueToTranslationKey(row.transactionKind)}`)}</>;
      }
    },
    {
      accessor: 'origin',
      title: t('table headers.origin'),
      renderCell: (row: FlexflowTransaction) => {
        const isTransactionDeclinedOrCancelled =
          row.status === TRANSACTION_STATUS.DECLINED || row.status === TRANSACTION_STATUS.CANCELLED;
        const isSbAssignmentTopUp =
          row.transactionKind === TRANSACTION_KIND.SB_ASSIGNMENT &&
          row.walletTransactionType === WALLET_TRANSACTION_TYPE.TOP_UP;

        if (isTransactionDeclinedOrCancelled) {
          return <div>-</div>;
        }

        if (isSbAssignmentTopUp) {
          return <>{t(`transaction kind.values.${mapApiEnumValueToTranslationKey(TRANSACTION_KIND.SB_ASSIGNMENT)}`)}</>;
        }

        if (row.transactionType === TRANSACTION_TYPE.CREDIT) {
          return <div>-</div>;
        }

        if (!row.transactionOrigin) {
          return <div>-</div>;
        }

        return <>{t(`origin.values.${mapApiEnumValueToTranslationKey(row.transactionOrigin)}`)}</>;
      }
    },
    {
      accessor: 'amount',
      title: t('table headers.amount'),
      getTableCellProps: () => ({
        sx: {
          textAlign: 'right'
        }
      }),
      renderCell: (row: FlexflowTransaction) => {
        const amount = formatCurrency(locale, currency, 2, 'exceptZero')(invertSign(Number(row.amount)));
        const tooltipText = getAmountTooltipText(row, t, locale, currency);

        if (!tooltipText) {
          return <>{amount}</>;
        }

        return (
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            <span>{amount}</span>
            <InfoTooltip title={tooltipText} arrow placement="top">
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  cursor: 'help'
                }}
              />
            </InfoTooltip>
          </Box>
        );
      }
    },
    {
      accessor: 'status',
      title: t('table headers.status'),
      renderCell: (row: FlexflowTransaction) => {
        const rowStatus = row.status.toLocaleLowerCase() as TRANSACTION_STATUS;
        return <TransactionStatus status={mapStatus(rowStatus)} />;
      }
    }
  ];
};
